/**
 * Edge Function Service Layer
 * 
 * All edge function calls from client code MUST go through this service.
 * This ensures compliance with the same-origin proxy policy.
 * 
 * Uses sameOrigin.fn() which:
 * - Routes through /api/functions/* in production (same-origin)
 * - Falls back to Supabase SDK in development only
 * 
 * STANDARDIZED RESPONSE PATTERN:
 * All edge functions return: { ok: boolean; data?: T; error?: EdgeError; request_id: string }
 * 
 * @see docs/MIGRATION_SUPABASE_SAME_ORIGIN.md for migration backlog
 */

import { sameOrigin } from '@/lib/sameOriginClient';

// =============================================================================
// STANDARD TYPES
// =============================================================================

/**
 * Standard error shape for edge function responses
 */
export interface EdgeError {
  code: string;
  message: string;
}

/**
 * Standard response shape for edge function calls
 * All edge functions should return this pattern.
 */
export interface EdgeResponse<T = unknown> {
  ok: boolean;
  data?: T;
  error?: EdgeError;
  request_id: string;
  /** For write operations: number of rows affected */
  affected?: number;
  /** For write operations: number of rows skipped */
  skipped?: number;
  /** True if this was a duplicate/idempotent request */
  duplicate?: boolean;
}

// =============================================================================
// NOTIFICATION SERVICES
// =============================================================================

interface SendNotificationParams {
  articleId: string;
  articleTitle: string;
  articleSlug: string;
  contentType: string;
  heroImageUrl?: string;
  metaDescription?: string;
}

interface SendNotificationResponse {
  successCount: number;
  failedCount?: number;
}

/**
 * Send notification emails when content is published
 */
export async function sendContentNotification(
  params: SendNotificationParams
): Promise<{ data: SendNotificationResponse | null; error: Error | null }> {
  return sameOrigin.fn<SendNotificationResponse>('send-notification', {
    body: params,
  });
}

// =============================================================================
// DEALER SERVICES
// =============================================================================

interface SendSalesTeamWelcomeParams {
  sales_team_member_id: string;
}

/**
 * Send welcome email to new sales team member
 */
export async function sendSalesTeamWelcome(
  params: SendSalesTeamWelcomeParams
): Promise<{ data: unknown; error: Error | null }> {
  return sameOrigin.fn('send-sales-team-welcome', {
    body: params,
  });
}

// =============================================================================
// BUYER SERVICES
// =============================================================================

interface SendBuyerWelcomeEmailParams {
  buyerName: string;
  buyerEmail: string;
}

/**
 * Send welcome email to a newly verified buyer
 */
export async function sendBuyerWelcomeEmail(
  params: SendBuyerWelcomeEmailParams
): Promise<{ data: unknown; error: Error | null }> {
  return sameOrigin.fn('send-buyer-welcome-email', {
    body: params,
  });
}

// =============================================================================
// CONTACT SERVICES
// =============================================================================

interface SendContactNotificationParams {
  firstName: string;
  lastName: string;
  email: string;
  subject: string;
  message: string;
}

/**
 * Send notification when a contact form is submitted
 */
export async function sendContactNotification(
  params: SendContactNotificationParams
): Promise<{ data: unknown; error: Error | null }> {
  return sameOrigin.fn('send-contact-notification', {
    body: params,
  });
}

// =============================================================================
// DEALER REGISTRATION SERVICES
// =============================================================================

interface SendDealerRegistrationEmailParams {
  dealership_name: string;
  contact_name: string;
  email: string;
}

/**
 * Send confirmation email after dealer registration
 */
export async function sendDealerRegistrationEmail(
  params: SendDealerRegistrationEmailParams
): Promise<{ data: unknown; error: Error | null }> {
  return sameOrigin.fn('send-dealer-registration-email', {
    body: params,
  });
}

interface SendAdminDealerRegistrationNotificationParams {
  dealership_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
  oem_brands: string[];
  city?: string;
  state?: string;
  website?: string;
}

/**
 * Send admin notification when a new dealer registers
 */
export async function sendAdminDealerRegistrationNotification(
  params: SendAdminDealerRegistrationNotificationParams
): Promise<{ data: unknown; error: Error | null }> {
  return sameOrigin.fn('send-admin-dealer-registration-notification', {
    body: params,
  });
}

// =============================================================================
// INVITE SERVICES
// =============================================================================

interface ValidateInviteParams {
  token: string;
}

interface ValidateInviteResponse {
  valid: boolean;
  error?: string;
  data?: {
    member_id: string;
    first_name: string;
    last_name: string;
    email: string;
    dealership_name: string;
  };
}

/**
 * Validate a sales team invite token
 */
export async function validateInvite(
  params: ValidateInviteParams
): Promise<{ data: ValidateInviteResponse | null; error: Error | null }> {
  return sameOrigin.fn<ValidateInviteResponse>('validate-invite', {
    body: params,
  });
}

interface AcceptInviteParams {
  token: string;
  user_id: string;
}

interface AcceptInviteResponse {
  success: boolean;
  error?: string;
}

/**
 * Accept a sales team invite and link user to dealership
 */
export async function acceptInvite(
  params: AcceptInviteParams
): Promise<{ data: AcceptInviteResponse | null; error: Error | null }> {
  return sameOrigin.fn<AcceptInviteResponse>('accept-invite', {
    body: params,
  });
}

// =============================================================================
// DEAL NOTIFICATION SERVICES
// =============================================================================

interface SendCounterReplyParams {
  deal_id: string;
  message: string;
  from: 'buyer' | 'dealer';
}

/**
 * Send a counter-offer reply message
 */
export async function sendCounterReply(
  params: SendCounterReplyParams
): Promise<{ data: unknown; error: Error | null }> {
  return sameOrigin.fn('send-counter-reply', {
    body: params,
  });
}

interface SendBuyerConfirmationNotificationParams {
  deal_id: string;
  dealer_account_id: string;
}

/**
 * Notify dealer that buyer confirmed the deal
 */
export async function sendBuyerConfirmationNotification(
  params: SendBuyerConfirmationNotificationParams
): Promise<{ data: unknown; error: Error | null }> {
  return sameOrigin.fn('send-buyer-confirmation-notification', {
    body: params,
  });
}

interface SendDealerLeadNotificationParams {
  deal_id: string;
  notification_type: string;
}

/**
 * Send notification to dealer about lead updates
 */
export async function sendDealerLeadNotification(
  params: SendDealerLeadNotificationParams
): Promise<{ data: unknown; error: Error | null }> {
  return sameOrigin.fn('send-dealer-lead-notification', {
    body: params,
  });
}

// =============================================================================
// ADMIN SERVICES
// =============================================================================

/**
 * Bootstrap admin role for allowed admin emails
 * 
 * NOTE: This is called from AuthContext during authentication.
 * It only runs for users with allowed admin emails.
 * The edge function validates the JWT before granting admin role.
 * 
 * Uses sameOrigin.fn() which automatically includes auth headers.
 */
export async function bootstrapAdminRole(
  _accessToken: string // Kept for API compatibility, but sameOrigin.fn handles auth
): Promise<{ data: unknown; error: Error | null }> {
  return sameOrigin.fn('bootstrap-admin-role', {
    body: {},
  });
}

// =============================================================================
// BOOTSTRAP & REFERENCE DATA SERVICES
// =============================================================================

export interface BootstrapResponse {
  user: { id: string; email: string | null } | null;
  persona: 'buyer' | 'dealer_admin' | 'salesperson' | 'admin' | null;
  profile: {
    buyerProfile?: unknown;
    dealerProfile?: unknown;
  };
  permissions: {
    isAdmin: boolean;
    isDealer: boolean;
    isBuyer: boolean;
    canManageTeam: boolean;
    canViewAllLeads: boolean;
  };
  counts: Record<string, number>;
  recentDeals: unknown[];
}

/**
 * Bootstrap user session data (profile, permissions, counts, recent deals)
 */
export async function fetchBootstrap(): Promise<{ data: BootstrapResponse | null; error: Error | null }> {
  return sameOrigin.fn<BootstrapResponse>('bootstrap');
}

export interface VehicleReferenceData {
  makes: string[];
  models: Record<string, string[]>;
  trims: Record<string, Record<string, string[]>>;
  modelYears: number[];
  lastUpdated: string;
}

/**
 * Fetch vehicle reference data (makes, models, trims)
 */
export async function fetchReferenceData(
  year: number
): Promise<{ data: VehicleReferenceData | null; error: Error | null }> {
  return sameOrigin.fn<VehicleReferenceData>('reference-data', {
    body: { year },
  });
}

// =============================================================================
// DEAL SERVICES
// =============================================================================

export interface SharedDealResponse {
  id: string;
  public_id: string | null;
  vehicle_year: number;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_trim: string | null;
  vehicle_msrp_cents: number | null;
  deal_type: string;
  deal_mode: string;
  status: string;
  inputs_json: unknown;
  outputs_json: unknown;
  created_at: string;
  updated_at: string;
  share_token: string | null;
}

/**
 * Fetch a shared deal by its share token
 */
export async function fetchSharedDeal(
  shareToken: string
): Promise<{ data: SharedDealResponse | null; error: Error | null }> {
  return sameOrigin.fn<SharedDealResponse>('get-shared-deal', {
    body: { share_token: shareToken },
  });
}

// =============================================================================
// DEALERSHIP SERVICES
// =============================================================================

export interface DealerResult {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  phone?: string;
  source: 'database' | 'firecrawl';
}

export interface LookupDealershipsResponse {
  success: boolean;
  oem: string;
  zip_code: string;
  source: 'database' | 'firecrawl' | 'hybrid';
  count: number;
  dealerships: DealerResult[];
  error?: string;
}

/**
 * Lookup dealerships by OEM (with fallback to external sources)
 */
export async function lookupDealerships(
  oem: string,
  zipCode?: string
): Promise<{ data: LookupDealershipsResponse | null; error: Error | null }> {
  return sameOrigin.fn<LookupDealershipsResponse>('lookup-dealerships', {
    body: { oem, zip_code: zipCode },
  });
}

// =============================================================================
// SALES TEAM SERVICES
// =============================================================================

interface SendSalesTeamInviteParams {
  sales_team_member_id: string;
}

/**
 * Send invite email to a sales team member
 */
export async function sendSalesTeamInvite(
  params: SendSalesTeamInviteParams
): Promise<{ data: unknown; error: Error | null }> {
  return sameOrigin.fn('send-sales-team-invite', {
    body: params,
  });
}

// =============================================================================
// BUYER STATUS NOTIFICATION SERVICES
// =============================================================================

interface SendBuyerStatusNotificationParams {
  deal_id: string;
  new_status: string;
  dealer_name: string;
  counter_message?: string;
}

/**
 * Send status notification to buyer (viewed, approved, denied, counter)
 */
export async function sendBuyerStatusNotification(
  params: SendBuyerStatusNotificationParams
): Promise<{ data: unknown; error: Error | null }> {
  return sameOrigin.fn('send-buyer-status-notification', {
    body: params,
  });
}

// =============================================================================
// ADMIN CONTENT GENERATION SERVICES
// =============================================================================

/**
 * Generate article drafts using AI
 */
export async function generateArticles(): Promise<{ 
  data: { success: boolean; articles?: unknown[]; error?: string } | null; 
  error: Error | null 
}> {
  return sameOrigin.fn('generate-articles');
}

interface ScrapeVehicleSpecsParams {
  url: string;
}

interface ScrapeVehicleSpecsResponse {
  success: boolean;
  data?: { markdown: string };
  error?: string;
}

/**
 * Scrape vehicle specs from a URL
 */
export async function scrapeVehicleSpecs(
  params: ScrapeVehicleSpecsParams
): Promise<{ data: ScrapeVehicleSpecsResponse | null; error: Error | null }> {
  return sameOrigin.fn<ScrapeVehicleSpecsResponse>('scrape-vehicle-specs', {
    body: params,
  });
}

interface DetectVehicleTrimsParams {
  scrapedContent: string;
  make: string;
  model: string;
  modelYear: string;
}

interface DetectedTrim {
  name: string;
  msrp_dollars: number | null;
  powertrain: string | null;
  drivetrain: string | null;
  key_features: string[];
}

interface DetectVehicleTrimsResponse {
  success: boolean;
  data?: {
    trims: DetectedTrim[];
    base_make?: string;
    base_model?: string;
    base_year?: number;
  };
  error?: string;
}

/**
 * Detect vehicle trims from scraped content
 */
export async function detectVehicleTrims(
  params: DetectVehicleTrimsParams
): Promise<{ data: DetectVehicleTrimsResponse | null; error: Error | null }> {
  return sameOrigin.fn<DetectVehicleTrimsResponse>('detect-vehicle-trims', {
    body: params,
  });
}

interface GenerateReviewDraftParams {
  vehicleInfo: string;
  scrapedContent?: string | null;
  tone?: string;
}

interface GeneratedReview {
  title_h1: string;
  meta_title: string;
  meta_description: string;
  bottom_line: string;
  key_highlights: string[];
  pros: string[];
  cons: string[];
  intro_html: string;
  performance_html: string;
  interior_html: string;
  tech_html: string;
  safety_html: string;
  value_html: string;
  ideal_for: string[];
  real_world_fit: string[];
  suggested_segment: string;
  msrp_cents: number | null;
}

interface GenerateReviewDraftResponse {
  success: boolean;
  data?: GeneratedReview;
  error?: string;
}

/**
 * Generate a vehicle review draft using AI
 */
export async function generateReviewDraft(
  params: GenerateReviewDraftParams
): Promise<{ data: GenerateReviewDraftResponse | null; error: Error | null }> {
  return sameOrigin.fn<GenerateReviewDraftResponse>('generate-review-draft', {
    body: params,
  });
}

interface RegenerateBottomLineParams {
  vehicleInfo: string;
}

interface RegenerateBottomLineResponse {
  success: boolean;
  bottom_line?: string;
  error?: string;
}

/**
 * Regenerate the bottom line summary for a vehicle review
 */
export async function regenerateBottomLine(
  params: RegenerateBottomLineParams
): Promise<{ data: RegenerateBottomLineResponse | null; error: Error | null }> {
  return sameOrigin.fn<RegenerateBottomLineResponse>('regenerate-bottom-line', {
    body: params,
  });
}

interface SendDealerApprovalEmailParams {
  dealer_account_id: string;
  base_url: string;
}

/**
 * Send approval email to dealer with setup link
 */
export async function sendDealerApprovalEmail(
  params: SendDealerApprovalEmailParams
): Promise<{ data: unknown; error: Error | null }> {
  return sameOrigin.fn('send-dealer-approval-email', {
    body: params,
  });
}

interface ScrapeOemDataParams {
  make: string;
  model_url?: string;
  year?: number;
}

interface ScrapedVehicle {
  model: string;
  trims?: Array<{
    name?: string;
    trim?: string;
    msrp_cents?: number;
    msrp?: number;
    invoice_cents?: number;
    invoice?: number;
    destination_cents?: number;
    destination?: number;
    segment?: string;
    body_style?: string;
    drivetrain?: string;
    engine?: string;
    transmission?: string;
    horsepower?: number;
    torque_lb_ft?: number;
    fuel_type?: string;
    mpg_city?: number;
    mpg_highway?: number;
    mpg_combined?: number;
    range_miles?: number;
    seating_capacity?: number;
    cargo_volume_cu_ft?: number;
    exterior_colors?: string[];
    interior_colors?: string[];
    source_url?: string;
  }>;
}

export interface ScrapeOemDataResponse {
  success: boolean;
  data?: {
    vehicles: ScrapedVehicle[];
  };
  error?: string;
}

/**
 * Scrape OEM data for vehicle trims
 */
export async function scrapeOemData(
  params: ScrapeOemDataParams
): Promise<{ data: ScrapeOemDataResponse | null; error: Error | null }> {
  return sameOrigin.fn<ScrapeOemDataResponse>('scrape-oem-data', {
    body: params,
  });
}

// =============================================================================
// ARTICLE ANALYSIS SERVICES
// =============================================================================

/**
 * Parameters for article analysis
 */
export interface AnalyzeArticleParams {
  title?: string;
  subtitle?: string;
  body?: string;
  existingCategories?: string[];
  existingTags?: string[];
  existingOems?: string[];
}

/**
 * Response data from article analysis
 */
export interface AnalyzeArticleData {
  meta_title?: string;
  meta_description?: string;
  suggested_categories?: string[];
  suggested_tags?: string[];
  suggested_oems?: string[];
}

/**
 * AI-powered article analysis for SEO and taxonomy suggestions.
 * Returns standardized EdgeResponse pattern.
 */
export async function analyzeArticle(
  params: AnalyzeArticleParams
): Promise<{ data: AnalyzeArticleData | null; error: Error | null }> {
  return sameOrigin.fn<AnalyzeArticleData>('analyze-article', {
    body: params,
  });
}
// =============================================================================
// ADMIN DEAL STATUS SERVICES
// =============================================================================

interface AdminUpdateDealStatusParams {
  deal_id: string;
  new_status: 'closed' | 'awaiting_confirmation' | 'responded';
  reason?: string;
  counter_terms?: Record<string, unknown>;
  counter_message?: string;
}

interface AdminUpdateDealStatusSuccess {
  ok: true;
  deal: {
    id: string;
    status: string;
    updated_at: string;
  };
}

interface AdminUpdateDealStatusError {
  ok: false;
  error: 'NOT_AUTHORIZED' | 'NOT_FOUND' | 'NO_ROWS_UPDATED' | 'INVALID_INPUT' | 'DATABASE_ERROR';
  message: string;
}

export type AdminUpdateDealStatusResponse = AdminUpdateDealStatusSuccess | AdminUpdateDealStatusError;

/**
 * Admin-only: Update deal status (approve, decline, counter)
 * This is server-authoritative - admin role is verified server-side.
 * 
 * Uses sameOrigin.fn() which:
 * - In preview: uses supabase.functions.invoke() (auto-includes auth)
 * - In production: uses /api/functions/* same-origin proxy
 * - Has hardened response parsing with detailed errors
 */
export async function adminUpdateDealStatus(
  params: AdminUpdateDealStatusParams,
  _accessToken: string // Kept for API compatibility, but sameOrigin.fn handles auth
): Promise<{ data: AdminUpdateDealStatusResponse | null; error: Error | null }> {
  return sameOrigin.fn<AdminUpdateDealStatusResponse>('admin-update-deal-status', {
    method: 'POST',
    body: params,
  });
}

// =============================================================================
// BUYER NOTIFICATION SERVICES
// =============================================================================

interface BuyerMarkNotificationReadParams {
  notification_id?: string;
  mark_all?: boolean;
}

interface BuyerMarkNotificationReadSuccess {
  ok: true;
  updated_count: number;
  notification_ids: string[];
}

interface BuyerMarkNotificationReadError {
  ok: false;
  error: 'NOT_AUTHORIZED' | 'NOT_FOUND' | 'NO_ROWS_UPDATED' | 'INVALID_INPUT' | 'DATABASE_ERROR';
  message: string;
}

export type BuyerMarkNotificationReadResponse = BuyerMarkNotificationReadSuccess | BuyerMarkNotificationReadError;

/**
 * Buyer-only: Mark notification(s) as read
 * This is server-authoritative - ownership is verified server-side.
 */
export async function buyerMarkNotificationRead(
  params: BuyerMarkNotificationReadParams,
  _accessToken: string // Kept for API compatibility, but sameOrigin.fn handles auth
): Promise<{ data: BuyerMarkNotificationReadResponse | null; error: Error | null }> {
  // Use the centralized sameOrigin.fn which:
  // - In preview: uses supabase.functions.invoke()
  // - In production: uses /api/functions/* same-origin proxy
  // - Has hardened response parsing with detailed errors
  return sameOrigin.fn<BuyerMarkNotificationReadResponse>('buyer-mark-notification-read', {
    body: params,
  });
}
